import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { CaretDown } from "@phosphor-icons/react";

const DIFF_COLOR = { Easy: "bg-secondary text-foreground", Medium: "bg-primary/10 text-primary", Hard: "bg-accent/10 text-accent" };

export default function Interview() {
  const [items, setItems] = useState([]);
  const [topic, setTopic] = useState("All");
  const [diff, setDiff] = useState("All");
  const [open, setOpen] = useState({});

  useEffect(() => { api.get("/interview").then((r) => setItems(r.data)); }, []);

  const topics = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.topic)))], [items]);
  const diffs = ["All", "Easy", "Medium", "Hard"];
  const filtered = items.filter((i) =>
    (topic === "All" || i.topic === topic) && (diff === "All" || i.difficulty === diff)
  );

  return (
    <div className="space-y-8">
      <header className="fade-up">
        <div className="overline mb-3">Interview Q&A Bank</div>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.95]">Drill it until it's muscle memory.</h1>
        <p className="text-muted-foreground mt-4 max-w-2xl">
          Curated questions across NIST, ISO 42001, EU AI Act, third-party assessment, and technical scenarios — with model answers.
        </p>
      </header>

      <div className="flex flex-wrap gap-3 border-b border-border pb-4">
        <FilterGroup label="Topic" value={topic} setValue={setTopic} options={topics} testId="filter-topic" />
        <FilterGroup label="Difficulty" value={diff} setValue={setDiff} options={diffs} testId="filter-difficulty" />
        <div className="ml-auto text-sm text-muted-foreground self-end" data-testid="interview-count">
          {filtered.length} question{filtered.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="border-t border-border">
        {filtered.map((q) => {
          const isOpen = !!open[q.id];
          return (
            <div key={q.id} className="border-b border-border bg-white" data-testid={`qa-item-${q.id}`}>
              <button onClick={() => setOpen((o) => ({ ...o, [q.id]: !o[q.id] }))}
                className="w-full text-left p-6 flex items-start justify-between gap-4 hover:bg-secondary/50 transition-colors"
                data-testid={`qa-toggle-${q.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 ${DIFF_COLOR[q.difficulty]}`}>{q.difficulty}</span>
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{q.topic}</span>
                  </div>
                  <div className="text-lg font-medium leading-snug">{q.question}</div>
                </div>
                <CaretDown size={18} className={`shrink-0 mt-2 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-6 pb-6 -mt-2 border-t border-border pt-4 bg-secondary/40">
                  <div className="overline mb-2">Model answer</div>
                  <p className="text-sm leading-relaxed text-foreground/90">{q.answer}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterGroup({ label, value, setValue, options, testId }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => (
          <button key={o} onClick={() => setValue(o)} data-testid={`${testId}-${o}`}
            className={`text-xs px-3 py-1 border ${value === o ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}
